import { Eye, RotateCcw, DollarSign, Package, User, FileText, Truck, Printer, Download, Copy, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { Badge } from '../components/ui/badge'
import { GlobalFilterBar, FilterValues } from '../components/ui/GlobalFilterBar'
import { SaleDetailModal } from '../components/sales/modals/SaleDetailModal'
import { ReturnSaleModal } from '../components/sales/modals/ReturnSaleModal'
import { CancelSaleModal } from '../components/sales/modals/CancelSaleModal'
import { CancelCfdiModal } from '../components/sales/modals/CancelCfdiModal'
import { InvoiceSaleModal, DownloadInvoiceModal } from '../components/sales/modals/InvoiceSaleModal'
import { GlobalInvoiceWizard } from '../components/sales/modals/GlobalInvoiceWizard'
import { InvoicesHistoryModal } from '../components/sales/modals/InvoicesHistoryModal'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { Sale, salesService } from '../services/sales.service'
import { banksService, BankItem } from '../services/banks.service'
import { useEffect } from 'react'
import { formatShortTicket, copyToClipboard } from '../lib/utils'
import { useSessionStore } from '../store/auth/auth.store'

export function Sales() {
  const canViewSaleDetail = useSessionStore((state) => state.canAction('sales.sale.detail'))
  const canPrintTicket = useSessionStore((state) => state.canAction('sales.ticket.print'))
  const canCreateSaleReturn = useSessionStore((state) => state.canAction('sales.sale-return.create'))
  const canCancelSale = useSessionStore((state) => state.canAction('sales.sale.cancel'))
  const canInvoiceSale = useSessionStore((state) => state.canAction('sales.sale.invoice'))
  const canInvoiceGlobalSale = useSessionStore((state) => state.canAction('sales.global-sale.invoice'))
  const canViewInvoices = useSessionStore((state) => state.canAction('sales.invoice.view'))

  const [filters, setFilters] = useState<FilterValues>({
    search: '',
    branch: '',
    status: 'all',
    seller: '',
    delivery: '',
    payment: 'all',
    bank: 'all',
    invoice: 'all',
    saleType: 'all',
    startDate: '',
    endDate: ''
  })

  const [page, setPage] = useState(1)

  const [banks, setBanks] = useState<BankItem[]>([])
  useEffect(() => {
    banksService.getItems({ all: true, is_active: true }).then(res => setBanks(res.data || res || []))
  }, [])
  const [selectedSale, setSelectedSale] = useState<any>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showReturnModal, setShowReturnModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [returnType, setReturnType] = useState<'partial' | 'total'>('partial')
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [showDownloadModal, setShowDownloadModal] = useState(false)
  const [showGlobalInvoiceWizard, setShowGlobalInvoiceWizard] = useState(false)
  const [showInvoicesHistoryModal, setShowInvoicesHistoryModal] = useState(false)
  const [printQueue, setPrintQueue] = useState<string | number | null>(null)
  // Estado para cancelación de CFDI standalone
  const [cancelCfdiTarget, setCancelCfdiTarget] = useState<{ cfdiId: string; saleId: string | number } | null>(null)

  const { data: salesData, isLoading } = useQuery({
    queryKey: [
      'sales',
      page,
      filters
    ],
    queryFn: () =>
      salesService.getSales({
        page,
        per_page: 10,
        search: filters.search || undefined,
        sucursal_id: filters.branch || undefined,
        status: filters.status !== 'all' ? filters.status : undefined,
        seller_id: filters.seller || undefined,
        delivery_user_id: filters.delivery || undefined,
        payment_method_id: filters.payment !== 'all' ? filters.payment : undefined,
        bank_name: filters.bank !== 'all' ? filters.bank : undefined,
        invoice_status: filters.invoice !== 'all' ? filters.invoice : undefined,
        sale_type: filters.saleType !== 'all' ? filters.saleType : undefined,
        date_from: filters.startDate || undefined,
        date_to: filters.endDate || undefined,
      }),
  })

  const salesList = salesData?.data || []
  const totalItems = salesData?.total || 0
  const currentPage = salesData?.current_page || 1
  const summary = salesData?.summary || {}
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-700'
      case 'PARTIALLY_REFUNDED':
        return 'bg-yellow-100 text-yellow-700'
      case 'REFUNDED':
        return 'bg-red-100 text-red-700'
      case 'CANCELLED':
        return 'bg-slate-100 text-slate-500'
      default:
        return 'bg-slate-100 text-slate-700'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'Completada'
      case 'PARTIALLY_REFUNDED':
        return 'Devolución Parcial'
      case 'REFUNDED':
        return 'Devolución Total'
      case 'CANCELLED':
        return 'Cancelada'
      default:
        return status
    }
  }

  const openDetailModal = (sale: Sale, autoPrint = false) => {
    if (!canViewSaleDetail || (autoPrint && !canPrintTicket)) return

    setSelectedSale(sale)
    if (autoPrint) setPrintQueue(sale.id)
    setShowDetailModal(true)
  }

  const openReturnModal = (sale: any, type: 'partial' | 'total') => {
    if (!canCreateSaleReturn || !canViewSaleDetail) return

    setSelectedSale(sale)
    setReturnType(type)
    setShowReturnModal(true)
  }

  const openCancelModal = (sale: any) => {
    if (!canCancelSale) return

    setSelectedSale(sale)
    setShowCancelModal(true)
  }

  const openInvoiceModal = (sale: Sale) => {
    if (!canInvoiceSale) return

    setSelectedSale(sale)
    setShowInvoiceModal(true)
  }

  const openDownloadModal = (sale: Sale) => {
    if (!canViewInvoices) return

    setSelectedSale(sale)
    setShowDownloadModal(true)
  }


  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Registro de Ventas
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {totalItems} ventas registradas
          </p>
        </div>
        <div className="flex gap-2">
          {canViewInvoices && <button
            onClick={() => setShowInvoicesHistoryModal(true)}
            className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition-all hover:border-slate-300 hover:bg-slate-50 shrink-0 shadow-sm"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 group-hover:bg-slate-200 transition-colors">
              <FileText className="h-5 w-5 text-slate-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700">Historial Facturas</p>
              <p className="text-xs text-slate-500">Consulta y descargas</p>
            </div>
          </button>}

          {canInvoiceGlobalSale && <button
            onClick={() => setShowGlobalInvoiceWizard(true)}
            className="group flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-left transition-all hover:border-primary/60 hover:bg-primary/10 shrink-0 shadow-sm"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 group-hover:bg-primary/25 transition-colors">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-primary">Factura Global</p>
              <p className="text-xs text-slate-500">Público en General</p>
            </div>
          </button>}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-border bg-card p-6 text-card-foreground shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-500/10 p-2">
              <DollarSign className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-table-foreground/70">Ventas de hoy</p>
              <p className="text-2xl font-semibold text-card-foreground">
                {typeof summary.sales_today_total === 'string' ? summary.sales_today_total : `$${(summary.sales_today_total || 0).toFixed(2)}`}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6 text-card-foreground shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <Package className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-table-foreground/70">Transacciones de hoy</p>
              <p className="text-2xl font-semibold text-card-foreground">{summary.transactions_count || 0}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6 text-card-foreground shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-yellow-500/10 p-2">
              <RotateCcw className="h-5 w-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-table-foreground/70">Devoluciones de hoy</p>
              <p className="text-2xl font-semibold text-card-foreground">{summary.returns_count || 0}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6 text-card-foreground shadow-sm">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-purple-500/10 p-2">
              <User className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-table-foreground/70">Ticket promedio de hoy</p>
              <p className="text-2xl font-semibold text-card-foreground">
                {typeof summary.average_ticket === 'string' ? summary.average_ticket : `$${(summary.average_ticket || 0).toFixed(2)}`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <GlobalFilterBar
        show={['search', 'branch', 'status', 'seller', 'delivery', 'payment', 'bank', 'invoice', 'saleType', 'dates']}
        dateShortcuts={['today', 'yesterday', '1month', '1year', '1week', '5years']}
        values={filters}
        onChange={(changes) => {
          setFilters(prev => ({ ...prev, ...changes }))
          setPage(1)
        }}
        customOptions={{
          searchPlaceholder: "Buscar ticket, cliente o medicamento...",
          statusOptions: [
            { value: 'COMPLETED', label: 'Completadas' },
            { value: 'PARTIALLY_REFUNDED', label: 'Devolución Parcial' },
            { value: 'REFUNDED', label: 'Devolución Total' }
          ],
          banksOptions: banks.map(b => b.name)
        }}
        onClearFilters={() => {
          setFilters({
            search: '',
            branch: '',
            status: 'all',
            seller: '',
            delivery: '',
            payment: 'all',
            bank: 'all',
            invoice: 'all',
            saleType: 'all',
            startDate: '',
            endDate: ''
          })
          setPage(1)
        }}
        hasActiveFilters={
          !!filters.search || !!filters.branch || filters.status !== 'all' || !!filters.seller ||
          !!filters.delivery || filters.payment !== 'all' || filters.bank !== 'all' || filters.invoice !== 'all' || filters.saleType !== 'all'
        }
      />

      {/* Sales Table */}
      {isLoading ? (
        <TableSkeleton columns={8} rows={5} />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-table-bg text-table-foreground shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-border bg-table-header-bg text-table-header-foreground">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-mediumtext-table-header-foreground uppercase">
                    Ticket
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-table-header-foreground uppercase">
                    Fecha y Hora
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-table-header-foreground uppercase">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-table-header-foreground uppercase">
                    Vendedor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-table-header-foreground uppercase">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-table-header-foreground uppercase">
                    Facturación
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-table-header-foreground uppercase">
                    Origen
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-table-header-foreground uppercase">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-table-header-foreground uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {salesList.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-slate-500">
                      No se encontraron ventas con los filtros actuales.
                    </td>
                  </tr>
                ) : (
                  salesList.map((sale) => (
                    <tr key={sale.id} className="transition-colors hover:bg-table-row-hover">
                      <td className="px-6 py-4 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-table-foreground" title={sale.ticket_number || String(sale.id)}>
                            {formatShortTicket(sale.ticket_number || sale.id)}
                          </span>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-md p-1 text-table-foreground hover:bg-slate-100 hover:text-table-foreground focus:outline-none focus:ring-2 focus:ring-slate-300"
                            onClick={async (e) => {
                              e.stopPropagation()
                              const success = await copyToClipboard(String(sale.ticket_number || sale.id))
                              if (success) {
                                toast.success('Ticket copiado al portapapeles')
                              } else {
                                toast.error('No se pudo copiar el ticket')
                              }
                            }}
                            title="Copiar ticket completo"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-table-foreground whitespace-nowrap">
                        <div className="font-medium">{sale.date}</div>
                        <div className="text-xs text-table-foreground/70">{sale.time}</div>
                      </td>
                      <td className="px-6 py-4 text-slate-700">
                        <div className="font-medium text-table-foreground truncate max-w-[200px]" title={sale.customer}>{sale.customer}</div>
                        {sale.deliveryPerson && (
                          <div 
                            className="mt-1.5 flex w-fit max-w-[200px] items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-2.5 py-0.5 text-[11px] font-medium text-primary"
                            title={`Repartidor: ${sale.deliveryPerson}`}
                          >
                            <Truck className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{sale.deliveryPerson}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-table-foreground whitespace-nowrap">{sale.seller}</td>
                      <td className="px-6 py-4 font-semibold text-table-foreground">
                        ${sale.total.toFixed(2)}
                      </td>
                      <td className="px-6 py-4">
                        {sale.invoiced ? (
                          <Badge className="bg-green-100 text-green-700">
                            Facturada
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700">
                            Sin facturar
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <Badge className="bg-slate-100 text-slate-700">POS</Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={getStatusColor(sale.status)}>
                          {getStatusLabel(sale.status)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          {canViewSaleDetail && <button
                            onClick={() => openDetailModal(sale)}
                            className="rounded p-1 hover:bg-table-row-hover"
                            title="Ver detalle"
                          >
                            <Eye className="h-4 w-4 text-card-foreground" />
                          </button>}
                          {canViewSaleDetail && canPrintTicket && <button
                            onClick={() => openDetailModal(sale, true)}
                            className="rounded p-1 hover:bg-table-row-hover"
                            title="Reimprimir ticket"
                          >
                            <Printer className="h-4 w-4 text-card-foreground" />
                          </button>}
                          {canInvoiceSale && !sale.invoiced && sale.status === 'COMPLETED' && (
                            <button
                              onClick={() => openInvoiceModal(sale)}
                              className="rounded p-1 hover:bg-table-row-hover"
                              title="Generar factura"
                            >
                              <FileText className="h-4 w-4 text-primary" />
                            </button>
                          )}

                          {/* Descargar: si ya está facturada */}
                          {canViewInvoices && sale.invoiced && (
                            <button
                              onClick={() => openDownloadModal(sale)}
                              className="rounded p-1 hover:bg-table-row-hover"
                              title="Descargar factura (PDF/XML)"
                            >
                              <Download className="h-4 w-4 text-green-600" />
                            </button>
                          )}

                          {/* Cancelar CFDI standalone — solo si está facturada y el CFDI no fue cancelado */}
                          {canCancelSale && sale.invoiced && sale.cfdi_id && !(sale as any).cfdi_cancelled_at && (
                            <button
                              onClick={() => setCancelCfdiTarget({ cfdiId: sale.cfdi_id!, saleId: sale.id })}
                              className="rounded p-1 hover:bg-red-50"
                              title="Cancelar CFDI ante el SAT"
                            >
                              <XCircle className="h-4 w-4 text-red-400 hover:text-red-600" />
                            </button>
                          )}

                          {canCreateSaleReturn && canViewSaleDetail && sale.status === 'COMPLETED' && (
                            <button
                              onClick={() => openReturnModal(sale, 'partial')}
                              className="rounded p-1 hover:bg-table-row-hover"
                              title="Procesar devolución"
                            >
                              <RotateCcw className="h-4 w-4 text-orange-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <GlobalPaginator
            totalItems={totalItems}
            itemsPerPage={10}
            currentPage={currentPage}
            from={salesData?.from}
            to={salesData?.to}
            onPageChange={(p) => setPage(p)}
          />
        </div>

      )}

      {/* Invoice Modal */}
      {canInvoiceSale && <InvoiceSaleModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        sale={selectedSale}
      />}

      {/* Download Invoice Modal */}
      {canViewInvoices && <DownloadInvoiceModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        sale={selectedSale}
      />}

      {/* Global Invoice Wizard */}
      {canInvoiceGlobalSale && <GlobalInvoiceWizard
        isOpen={showGlobalInvoiceWizard}
        onClose={() => setShowGlobalInvoiceWizard(false)}
        onSuccess={() => setShowGlobalInvoiceWizard(false)}
      />}

      {/* Invoices History Modal */}
      {canViewInvoices && <InvoicesHistoryModal
        isOpen={showInvoicesHistoryModal}
        onClose={() => setShowInvoicesHistoryModal(false)}
      />}

      {/* Detail Modal */}
      {canViewSaleDetail && <SaleDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false)
          setPrintQueue(null)
        }}
        sale={selectedSale}
        onOpenReturnModal={openReturnModal}
        onOpenCancelModal={openCancelModal}
        autoPrint={printQueue !== null && selectedSale?.id === printQueue}
        onPrintComplete={() => setPrintQueue(null)}
      />}

      {/* Return Modal */}
      {canCreateSaleReturn && canViewSaleDetail && <ReturnSaleModal
        isOpen={showReturnModal}
        onClose={() => setShowReturnModal(false)}
        sale={selectedSale}
        initialType={returnType}
      />}

      {/* Cancel Modal */}
      {canCancelSale && <CancelSaleModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        sale={selectedSale}
      />}

      {/* Cancel CFDI standalone Modal */}
      {canCancelSale && cancelCfdiTarget && (
        <CancelCfdiModal
          isOpen={!!cancelCfdiTarget}
          onClose={() => setCancelCfdiTarget(null)}
          cfdiId={cancelCfdiTarget.cfdiId}
          saleId={cancelCfdiTarget.saleId}
          onSuccess={() => setCancelCfdiTarget(null)}
        />
      )}
    </div>
  )
}
